import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import type { Schema as SchemaType } from 'effect';
import { Effect } from 'effect';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

import { HTTP_METHOD } from './_shared';

const ENDPOINT = '/api/v2/shop/product-variants/{code}/expand';
export type MakeGetProductVariantExpandedByCodeDeps<ProductVariantExpanded extends SchemaType.Top> =
  {
    readonly schemas: {
      readonly productVariantExpanded: ProductVariantExpanded;
    };
    readonly adapters: {
      readonly productVariantExpanded: {
        readonly inStock: (productVariant: ProductVariantExpanded['Type']) => boolean | undefined;
      };
    };
    readonly rawUrlEncode: (input: string) => string;
  };

export const makeGetProductVariantExpandedByCode = <ProductVariantExpanded extends SchemaType.Top>(
  deps: MakeGetProductVariantExpandedByCodeDeps<ProductVariantExpanded>
) => {
  const GetProductVariantExpandedByCodeError = createBackendApiError({
    domain: 'Product',
    endpoint: 'GetProductVariantExpandedByCode',
    path: ENDPOINT,
    identifierField: 'code',
  });

  const getProductVariantExpandedByCodeFetch = (code: string) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/ld+json')
        .fetch({ params: { code: deps.rawUrlEncode(code) } });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'productVariant.code': code,
        },
      })
    );

  const getProductVariantExpandedByCode = (code: string) =>
    getProductVariantExpandedByCodeFetch(code).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.withSpan('ValidateResponse'),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) => schemaBodyJsonClean(deps.schemas.productVariantExpanded)(response),
          404: (response) =>
            Effect.fail(new GetProductVariantExpandedByCodeError.NotFoundError({ code, response })),
          orElse: (response) =>
            Effect.fail(
              new GetProductVariantExpandedByCodeError.ApiRequestError({ code, response })
            ),
        })
      ),
      Effect.tap((result) =>
        logApiOperation(
          ApiModule.ProductVariantGetExpanded,
          'Debug',
          `Successfully resolved product variant for code: ${code} -> inStock: ${String(deps.adapters.productVariantExpanded.inStock(result))}`
        )
      ),
      Effect.withSpan('api.productVariant.getExpanded', {
        kind: 'internal',
        attributes: {
          'productVariant.code': code,
        },
      })
    );

  return { getProductVariantExpandedByCode, GetProductVariantExpandedByCodeError };
};
